import type { components, operations } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '@/lib/api'

/**
 * The typed boundary to the bank reconciliation endpoints under
 * `/api/accounting` (Wave 3 stream D).
 *
 * Every type here is GENERATED — `components['schemas']['BankStatementResource']`
 * and friends come from `openapi.json`. AGENTS.md §3: a type describing an API
 * response is generated, never authored. The summary and auto-match results
 * are inline response bodies, so they are read from `operations` the same way.
 *
 * The `X-Company-Id` header is what `SetTenantContext` reads to populate
 * `CompanyContext`. These routes carry no `{company}` path segment, so the
 * header is the only thing that says which tenant this is.
 */

export type BankStatement = components['schemas']['BankStatementResource']
export type BankStatementLine = components['schemas']['BankStatementLineResource']
export type BankAccount = components['schemas']['BankAccountResource']
export type StatementLineCandidate = NonNullable<BankStatementLine['candidates']>[number]

/**
 * The statement lifecycle values, and the list's URL-backed filters.
 *
 * `GET /accounting/bank-statements` exposes no query parameters
 * (`bankStatement.index` is `query?: never` in the generated contract), so
 * both are matched client-side over the fetched rows; the route owns the
 * values so a filtered view is linkable and survives a reload.
 */
export const STATEMENT_STATUSES = ['imported', 'reconciling', 'reconciled'] as const

export type StatementStatusValue = (typeof STATEMENT_STATUSES)[number]

export interface StatementFilters {
  q?: string
  status?: StatementStatusValue
}

/** The statement's reconciliation figures, as `ReconciliationSummary::for()` returns them. */
export type ReconciliationSummary =
  operations['reconciliation.summary']['responses'][200]['content']['application/json']['data']

/** How many statement lines the auto-match pass matched, and how many it left ambiguous. */
export type AutoMatchResult =
  operations['reconciliation.autoMatch']['responses'][200]['content']['application/json']['data']

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time — the same ref pattern as
 * `features/accounting-bills/api.ts`.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Prefix shared by every statement query key, so one invalidation hits all. */
function statementsKey(companyId: string | null) {
  return ['companies', companyId, 'accounting', 'bank-statements'] as const
}

/** The bank accounts, for the statement list's account labels. Same endpoint and query
 * key as `features/accounting-bank/api.ts` — the features share the cache,
 * never a module import (AGENTS.md: a feature never imports another feature).
 */
export function useBankAccounts(
  companyId: string | null,
  options?: Omit<UseQueryOptions<BankAccount[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BankAccount[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'bank-accounts'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<BankAccount[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/bank-accounts', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load bank accounts')
      return unwrap<BankAccount[]>(response.data ?? {}, 'bank-accounts') ?? []
    },
  })
}

export function useBankStatements(
  companyId: string | null,
  options?: Omit<UseQueryOptions<BankStatement[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BankStatement[], Error>({
    queryKey: statementsKey(companyId),
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<BankStatement[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/bank-statements', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load bank statements')
      }
      return unwrap<BankStatement[]>(response.data ?? {}, 'bank-statements') ?? []
    },
  })
}

export function useBankStatement(
  companyId: string | null,
  statementId: string | null,
  options?: Omit<UseQueryOptions<BankStatement | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BankStatement | null, Error>({
    queryKey: [...statementsKey(companyId), statementId] as const,
    enabled: Boolean(companyId && statementId),
    ...options,
    queryFn: async (): Promise<BankStatement | null> => {
      if (!companyId || !statementId) return null
      const response = await api.GET('/accounting/bank-statements/{statement}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { statement: statementId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load the bank statement')
      }
      return unwrap<BankStatement>(response.data ?? {}, 'bank-statement')
    },
  })
}

export function useReconciliationSummary(
  companyId: string | null,
  statementId: string | null,
  options?: Omit<UseQueryOptions<ReconciliationSummary | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ReconciliationSummary | null, Error>({
    queryKey: [...statementsKey(companyId), statementId, 'summary'] as const,
    enabled: Boolean(companyId && statementId),
    ...options,
    queryFn: async (): Promise<ReconciliationSummary | null> => {
      if (!companyId || !statementId) return null
      const response = await api.GET('/accounting/bank-statements/{statement}/summary', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { statement: statementId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load the reconciliation summary')
      }
      return unwrap<ReconciliationSummary>(response.data ?? {}, 'summary')
    },
  })
}

export function useAutoMatchStatement(
  companyId: string | null,
  options?: Omit<UseMutationOptions<AutoMatchResult, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<AutoMatchResult, Error, string>({
    ...options,
    mutationFn: async (statementId) => {
      const response = await api.POST('/accounting/bank-statements/{statement}/auto-match', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { statement: statementId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to auto-match')
      return unwrap<AutoMatchResult>(response.data ?? {}, 'auto-match')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: statementsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useMatchStatementLine(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<BankStatementLine, Error, { lineId: string; paymentId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<BankStatementLine, Error, { lineId: string; paymentId: string }>({
    ...options,
    mutationFn: async ({ lineId, paymentId }) => {
      const response = await api.POST('/accounting/statement-lines/{line}/match', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { line: lineId } },
        body: { source_type: 'payment', source_id: paymentId },
      })
      if (response.error) {
        const error: unknown = response.error
        const message =
          typeof error === 'string'
            ? error
            : typeof error === 'object' &&
                error !== null &&
                'message' in error &&
                typeof error.message === 'string'
              ? error.message
              : 'Failed to match line'
        throw new Error(message)
      }
      return unwrap<BankStatementLine>(response.data ?? {}, 'statement-lines')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: statementsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useUnmatchStatementLine(
  companyId: string | null,
  options?: Omit<UseMutationOptions<BankStatementLine, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<BankStatementLine, Error, string>({
    ...options,
    mutationFn: async (lineId) => {
      const response = await api.DELETE('/accounting/statement-lines/{line}/match', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { line: lineId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to unmatch line')
      return unwrap<BankStatementLine>(response.data ?? {}, 'statement-lines')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: statementsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useIgnoreStatementLine(
  companyId: string | null,
  options?: Omit<UseMutationOptions<BankStatementLine, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<BankStatementLine, Error, string>({
    ...options,
    mutationFn: async (lineId) => {
      const response = await api.POST('/accounting/statement-lines/{line}/ignore', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { line: lineId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to ignore line')
      return unwrap<BankStatementLine>(response.data ?? {}, 'statement-lines')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: statementsKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}
